import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentContainer = styled.section`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 1400px;
  margin: 0 auto;
`;

export const DashboardSection = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  padding: 1rem 1rem 1.1rem;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SectionSubtitle = styled.p`
  margin: 0.25rem 0 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
`;

export const StatCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 1rem;
  background: ${({ theme }) => theme.colors.background};
`;

export const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const StatValue = styled.div`
  margin-top: 0.4rem;
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const HorizontalBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const BarRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 48px;
  align-items: center;
  gap: 0.75rem;
`;

export const BarLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
`;

export const BarTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.border.light};
  overflow: hidden;
`;

export const BarFill = styled.div<{ $width: number }>`
  width: ${({ $width }) => `${$width}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
`;

export const BarValue = styled.span`
  text-align: right;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.85rem;
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

export const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const FiltersPanel = styled.div`
  margin-top: 0.75rem;
  padding: 0.8rem;
  border: 1px dashed ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const AppliedFiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const AppliedFilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.78rem;
  background: ${({ theme }) => theme.colors.background};
`;

export const Select = styled.select`
  min-width: 260px;
  padding: 0.55rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const DateInput = styled.input`
  min-width: 180px;
  padding: 0.55rem 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;

  th,
  td {
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    white-space: nowrap;
  }

  th {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  td {
    font-size: 0.88rem;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

export const UserCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

export const UserCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 1rem;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

export const UserCardTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const UserCardSubtle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.8rem;
`;

export const UserActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const UserActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const EmptyState = styled.div`
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.85rem;
`;
